import { BatchSetting, ProductSetting } from '../LineSettings.types'
import {
  editsFromRows,
  GridShape,
  LineEdits,
  LineKind,
  PendingRows,
} from '../lineRecalculation'

/**
 * Total production: Batch production and SKU production in one grid, each
 * batch group a parent row with its SKU groups under it.
 *
 * Both kinds of row share columns where they mean the same thing (a batch's
 * minimum batch size and a SKU group's MOQ are both "Min order"), so each
 * row is the API's row with its fields renamed (FIELDS). Edits are renamed
 * back and go to the API's cycle rules like any other line-settings edit.
 */

type Row = Record<string, unknown>

export type TotalRow = Row & {
  id: string
  rowKind: LineKind
  // Tree path: the batch, then the SKU group. Keys only, unique on the grid.
  path: string[]
  label: string
}

// Grid field for each API field that is shown under another name; all other
// fields keep their own.
const FIELDS: Record<LineKind, Record<string, string>> = {
  products: {
    allocatedSalesRate: 'salesRate',
    minimumProductionOrderQuantity: 'minOrder',
    minBatchCycleInput: 'batchMin',
    minCycleOverride: 'minOverride',
    maxCycleOverride: 'maxOverride',
    minCycleInput: 'minCycle',
    maxCycleInput: 'maxCycle',
    workcentreCode: 'workCentreCode',
  },
  batches: {
    minimumBatchSize: 'minOrder',
    initialBatchCycle: 'initialCycle',
    minBatchCycleOverride: 'minOverride',
    maxBatchCycleOverride: 'maxOverride',
    minBatchCycleInput: 'minCycle',
    maxBatchCycleInput: 'maxCycle',
  },
}

const API_FIELDS: Record<LineKind, Record<string, string>> = {
  products: Object.fromEntries(
    Object.entries(FIELDS.products).map(([api, grid]) => [grid, api])
  ),
  batches: Object.fromEntries(
    Object.entries(FIELDS.batches).map(([api, grid]) => [grid, api])
  ),
}

export const TOTAL_SHAPE: GridShape = {
  kinds: ['products', 'batches'],
  kindOf: (row) => row.rowKind as LineKind,
  fieldOf: (kind, field) => FIELDS[kind][field] ?? field,
}

const rename = (row: Row, names: Record<string, string>): Row =>
  Object.fromEntries(
    Object.entries(row).map(([field, value]) => [names[field] ?? field, value])
  )

const batchPath = (row: {
  baseSourceSiteKey: unknown
  workCentreKey: unknown
  batchGroupingCode: unknown
}) => `${row.baseSourceSiteKey}|${row.workCentreKey}|${row.batchGroupingCode}`

/** Every batch group, each followed by its SKU groups on the same line. */
export const toTotalRows = (
  batches: BatchSetting[],
  products: ProductSetting[]
): TotalRow[] => {
  const skuCount = new Map<string, number>()
  products.forEach((p) =>
    skuCount.set(batchPath(p), (skuCount.get(batchPath(p)) ?? 0) + 1)
  )
  const batchRows: TotalRow[] = batches.map((b) => ({
    ...rename(b as unknown as Row, FIELDS.batches),
    id: `batch|${batchPath(b)}`,
    rowKind: 'batches',
    path: [batchPath(b)],
    label: b.batchGroupingCode ?? '(no batch)',
    skuCount: skuCount.get(batchPath(b)) ?? 0,
  }))
  const skuRows: TotalRow[] = products.map((p) => ({
    ...rename(p as unknown as Row, FIELDS.products),
    id: `sku|${p.baseSourceSiteKey}|${p.workCentreKey}|${p.skuGroupKey}`,
    rowKind: 'products',
    path: [batchPath(p), String(p.skuGroupKey)],
    label: String(p.skuGroupCode ?? p.skuGroupKey),
  }))
  return [...batchRows, ...skuRows]
}

const KEYS: Record<LineKind, string[]> = {
  products: ['baseSourceSiteKey', 'workCentreKey', 'skuGroupKey'],
  batches: ['baseSourceSiteKey', 'workCentreKey', 'batchGroupingCode'],
}

/** One row's changed fields (grid names) as a line-settings edit: its keys
 *  and those fields under the API's names. */
export const apiEdit = (row: TotalRow, values: Row): Row => {
  const edit: Row = {}
  KEYS[row.rowKind].forEach((key) => (edit[key] = row[key]))
  Object.entries(values).forEach(
    ([field, value]) => (edit[API_FIELDS[row.rowKind][field] ?? field] = value)
  )
  return edit
}

/** The grid's pending rows as line-settings edits, under the API's names. */
export const totalEdits = (pending: PendingRows): LineEdits => {
  const pairs = (kind: LineKind) =>
    pending.current.flatMap((current, i) =>
      current.rowKind === kind
        ? [
            {
              current: rename(current, API_FIELDS[kind]),
              before: rename(pending.before[i] ?? current, API_FIELDS[kind]),
            },
          ]
        : []
    )
  return {
    products: editsFromRows('products', pairs('products')),
    batches: editsFromRows('batches', pairs('batches')),
  }
}
